// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — one player's run over one yard. Pure: driven by play()/undo()/tick(), never by the DOM
// or a clock of its own, which is what lets the race, the bot and the tests all drive the same
// code path.
//
// UNDO IS FREE AND REFUNDS THE MOVE. That is a deliberate choice, not an oversight. A shunting
// puzzle is a PLANNING game played on a phone with a thumb: a mis-tap that permanently cost a
// move would make the score a measure of dexterity, and a yard you could only explore by paying
// for it would push players into paralysis rather than thought. The score stays honest because
// the yard is scored on MOVES and ranked with TIME as the tie-break — undoing your way to par
// still means finding the optimal line, and it costs you the clock while you look for it.
//
// The run ends when the train is made up, when the player gives up, or when the round clock runs
// out. That last one exists so a race always terminates even if somebody wanders off mid-yard.

import type { Mode } from './modes';
import { generate, type Puzzle } from './generate';
import {
  applyMove,
  cloneYard,
  isLegal,
  isSolved,
  legalMoves,
  moveCost,
  seatedCount,
  type Move,
  type Yard,
} from './rules';

/** The outer horizon for a run. Generous — it exists to guarantee a race ends, not to hurry anyone. */
export const ROUND_LIMIT_MS = 6 * 60 * 1000;

export type EndReason = 'solved' | 'gaveup' | 'timeout';

export interface GameEvent {
  readonly t: 'push' | 'pull' | 'round' | 'seat' | 'unseat' | 'solve' | 'undo' | 'reject';
  /** How many cars the loco moved with (push/pull), or how many are correctly seated (seat). */
  readonly n?: number;
}

export interface GameState {
  readonly mode: Mode;
  readonly seed: number;
  readonly puzzle: Puzzle;
  readonly yard: Yard;
  readonly par: number;
  /** Every move applied so far, in order — the undo stack and the replay in one. */
  readonly history: Move[];
  /** The score: total cost of the moves currently applied. */
  moves: number;
  /** How many times the player backed out of a move. Shown on the results screen, never scored. */
  undos: number;
  seated: number;
  over: boolean;
  finished: boolean;
  reason: EndReason | null;
  elapsedMs: number;
  events: GameEvent[];
}

export function createGame(mode: Mode, seed: number): GameState {
  const puzzle = generate(mode, seed);
  const yard = cloneYard(puzzle.yard);
  return {
    mode,
    seed,
    puzzle,
    yard,
    par: puzzle.par,
    history: [],
    moves: 0,
    undos: 0,
    seated: seatedCount(yard),
    over: false,
    finished: false,
    reason: null,
    elapsedMs: 0,
    events: [],
  };
}

/** Every move the player may make right now. The renderer turns these into tap targets. */
export function movesAvailable(g: GameState): Move[] {
  return g.over ? [] : legalMoves(g.yard);
}

/**
 * Play one move. An illegal move is REJECTED rather than applied — the UI only ever offers legal
 * targets, so this is the belt-and-braces path for a stale tap during an animation.
 */
export function play(g: GameState, m: Move): boolean {
  if (g.over) return false;
  if (!isLegal(g.yard, m)) {
    g.events.push({ t: 'reject' });
    return false;
  }
  const before = g.seated;
  const carried = g.yard.cars[g.yard.locoAt].length;
  applyMove(g.yard, m);
  g.history.push(m);
  g.moves += moveCost(m);
  g.seated = seatedCount(g.yard);

  g.events.push(m.t === 'round' ? { t: 'round' } : m.t === 'push' ? { t: 'push', n: carried } : { t: 'pull', n: m.k });
  if (g.seated > before) g.events.push({ t: 'seat', n: g.seated });
  else if (g.seated < before) g.events.push({ t: 'unseat', n: g.seated });

  if (isSolved(g.yard)) finish(g, 'solved');
  return true;
}

/** Back out the last move, refunding its cost. Returns false when there is nothing to undo. */
export function undo(g: GameState): boolean {
  if (g.over || g.history.length === 0) return false;
  const history = g.history.slice(0, -1);
  const fresh = cloneYard(g.puzzle.yard);
  let cost = 0;
  for (const m of history) {
    applyMove(fresh, m);
    cost += moveCost(m);
  }
  g.yard.cars.length = 0;
  g.yard.cars.push(...fresh.cars);
  g.yard.locoAt = fresh.locoAt;
  g.history.length = 0;
  g.history.push(...history);
  g.moves = cost;
  g.undos++;
  g.seated = seatedCount(g.yard);
  g.events.push({ t: 'undo' });
  return true;
}

/** Put every car back where it started. Costs nothing but the clock. */
export function resetYard(g: GameState): void {
  if (g.over) return;
  const fresh = cloneYard(g.puzzle.yard);
  g.yard.cars.length = 0;
  g.yard.cars.push(...fresh.cars);
  g.yard.locoAt = fresh.locoAt;
  g.history.length = 0;
  g.moves = 0;
  g.undos++;
  g.seated = seatedCount(g.yard);
  g.events.push({ t: 'undo' });
}

export function giveUp(g: GameState): void {
  if (!g.over) finish(g, 'gaveup');
}

/** Advance the wall clock. Driven by the render loop, and by the race's setInterval in a hidden tab. */
export function tick(g: GameState, dtMs: number): void {
  if (g.over) return;
  g.elapsedMs += Math.max(0, dtMs);
  if (g.elapsedMs >= ROUND_LIMIT_MS) finish(g, 'timeout');
}

function finish(g: GameState, reason: EndReason): void {
  g.over = true;
  g.reason = reason;
  g.finished = reason === 'solved';
  if (reason === 'solved') g.events.push({ t: 'solve' });
}

export function drainEvents(g: GameState): GameEvent[] {
  if (g.events.length === 0) return [];
  const out = g.events.slice();
  g.events.length = 0;
  return out;
}

/** How far over the proven optimum the player is. Negative is impossible; zero is a perfect solve. */
export function overPar(g: GameState): number {
  return g.moves - g.par;
}

/** The live yard, for a renderer that wants to draw without reaching into the state object. */
export function yardOf(g: GameState): Yard {
  return g.yard;
}
